/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import dayjs from "dayjs";
import { useTenant } from "hooks/settings";
import { Loaded, loadedEndpoint } from "models/loaded";
import {
  SiopScheduleMixGroupPoint,
  TenantSiopScheduleData,
  TimeAggregation,
  useGetTenantSiopScheduleQuery,
} from "src/store/api/generatedApi";
import {
  GroupedScheduleSeries,
  ScheduleTimeSeries,
  ScheduleTimeSeriesKey,
} from "../types";

export const useStrategyDashboardSchedule = (
  start: string,
  end: string
): Loaded<GroupedScheduleSeries> => {
  const tenantName = useTenant();
  const fromDate = dayjs(start).format("YYYY-MM-DD");
  const toDate = dayjs(end).format("YYYY-MM-DD");
  const loadedRawData = loadedEndpoint(
    useGetTenantSiopScheduleQuery({
      tenantName,
      includeDaily: true,
      fromDate,
    })
  );
  if (loadedRawData.status === "success") {
    return {
      status: "success",
      data: buildGroupedScheduleSeries(loadedRawData.data, toDate),
      fetching: loadedRawData.fetching,
    };
  }
  return loadedRawData;
};

const buildGroupedScheduleSeries = (
  data: TenantSiopScheduleData,
  toDate: string
): GroupedScheduleSeries => {
  const mixGroupNames = data.mix_group_names ?? [];
  const dailyPoints = data.daily_mix_group_series ?? [];
  const monthlyPoints = data.mix_group_series ?? [];

  const items: [ScheduleTimeSeriesKey, ScheduleTimeSeries][] = [];
  const availableTimeAggregations = new Set<TimeAggregation>();

  const addSeries = (
    points: SiopScheduleMixGroupPoint[],
    timeAggregation: TimeAggregation
  ) => {
    const filtered = points.filter((p) => p.month_start <= toDate);
    if (filtered.length === 0) return;
    availableTimeAggregations.add(timeAggregation);

    const byMixGroup = new Map<string, { dates: string[]; masses: number[] }>();
    for (const point of filtered) {
      const group = byMixGroup.get(point.mix_group_name) ?? {
        dates: [],
        masses: [],
      };
      group.dates.push(point.month_start);
      group.masses.push(point.scheduled_tons);
      byMixGroup.set(point.mix_group_name, group);
    }

    for (const mixGroup of mixGroupNames) {
      const group = byMixGroup.get(mixGroup);
      if (!group) continue;
      items.push([
        { timeAggregation, mixGroup },
        { date: group.dates, scheduled_mass: group.masses },
      ]);
    }
  };

  addSeries(dailyPoints, "daily");
  addSeries(monthlyPoints, "monthly");

  return {
    items,
    availableTimeAggregations,
    availableMixGroups: mixGroupNames,
  };
};
